import { useCallback, useEffect, useRef, useState } from 'react';
import './ReadAloudControl.css';
import { useOptionalBriefingRoomServices } from '../../contexts/BriefingRoomContext';
import { useOptionalTtsQueue } from '../../contexts/TtsQueueContext';
import { useNarratorVoice } from '../../hooks/useNarratorVoice';
import { TtsUnavailableError } from '../../services/types';

export type ReadAloudState = 'idle' | 'loading' | 'playing' | 'error';

export interface ReadAloudControlProps {
  text: string;
  testidPrefix: string;
  // V1 ask add (2026-06-02): `voice` prop is an explicit override for
  // tests + a future per-NPC voice map. When undefined (the default at
  // every render site today), the component reads from
  // useNarratorVoice — the global Audio-panel picker's selection.
  voice?: string;
}

/**
 * V1 Voice GM (2026-06-02) — Read-Aloud toggle button + inline audio mini-player.
 *
 * Click the button → fetch /api/voice/tts (Kokoro by default, bm_lewis voice
 * unless overridden) → mount an <audio controls autoPlay> on the paragraph
 * card. Click again, or navigate to a new paragraph (the component unmounts
 * with the paragraph) → audio.pause + AbortController.abort + URL.revoke.
 *
 * Branches on services.voice.isAvailable() so the standalone build (M4
 * pending) hides the button until the standalone TTS path lands.
 *
 * Pattern callouts:
 * - One AbortController per fetch — replaced on every new click so a fast
 *   user (click → click → click) doesn't leak signals.
 * - cleanupRef pattern so unmount during loading cancels the network call.
 * - audioRef.current?.pause() is the imperative stop; setState alone is not
 *   enough to release Audio playback.
 * - <audio> mounts only when state === 'playing'. The browser starts
 *   playing on autoPlay; we listen for onEnded → idle.
 */
export function ReadAloudControl({ text, testidPrefix, voice }: ReadAloudControlProps) {
  const services = useOptionalBriefingRoomServices();
  const ttsQueue = useOptionalTtsQueue();
  const [selectedVoice] = useNarratorVoice();
  const effectiveVoice = voice ?? selectedVoice;
  const [state, setState] = useState<ReadAloudState>('idle');
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const revokeRef = useRef<(() => void) | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const cleanup = useCallback((opts?: { keepState?: boolean }) => {
    if (abortRef.current !== null) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    if (audioRef.current !== null) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    if (revokeRef.current !== null) {
      revokeRef.current();
      revokeRef.current = null;
    }
    setAudioUrl(null);
    if (!opts?.keepState) {
      setState('idle');
      setErrorMessage(null);
    }
  }, []);

  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  // V13 §11.16: TTS playback should be cancellable on paragraph change. Text
  // is the per-paragraph identity for this control; flushing on text change
  // makes the unmount-on-navigate guarantee explicit even when ReadAloudControl
  // is re-keyed by paragraph number upstream.
  useEffect(() => {
    cleanup();
    // cleanup intentionally not a dep — we re-cleanup on every text change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  const handleClick = useCallback(async () => {
    if (services === null || !services.voice.isAvailable()) {
      return;
    }
    // 2026-06-07 — Pause/Stop semantics fix. PRIOR BUG: this block
    // unconditionally called cancelAll + enqueue, so when the button
    // showed ⏸ (label "Stop reading") clicking it stopped the current
    // playback AND immediately re-enqueued the same text — the user
    // saw a pause-then-auto-restart. User report (verbatim 2026-06-07):
    // "when i click pause it pauses the auto restarts".
    //
    // Fix: check the effective playing/loading state FIRST. If the
    // button is currently showing ⏸, click is a STOP only (no enqueue).
    // If the button is showing ▶, click starts fresh playback.
    const queuePlayingNow =
      ttsQueue !== null && ttsQueue.state === 'playing';
    const queueLoadingNow =
      ttsQueue !== null && ttsQueue.state === 'loading';
    const effectivePlaying = queuePlayingNow || state === 'playing';
    const effectiveLoading = queueLoadingNow || state === 'loading';
    if (effectivePlaying || effectiveLoading) {
      // Stop semantics — kill current playback and stay stopped.
      if (ttsQueue !== null) {
        ttsQueue.cancelAll();
      } else {
        cleanup();
      }
      return;
    }
    // Idle state — start fresh playback.
    if (ttsQueue !== null) {
      ttsQueue.enqueue(text, { voice: effectiveVoice, source: 'readaloud' });
      return;
    }
    setState('loading');
    setErrorMessage(null);
    const ac = new AbortController();
    abortRef.current = ac;
    try {
      const result = await services.voice.speak(text, {
        voice: effectiveVoice,
        signal: ac.signal,
      });
      if (ac.signal.aborted) {
        result.revoke();
        return;
      }
      revokeRef.current = result.revoke;
      setAudioUrl(result.audioUrl);
      setState('playing');
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        return;
      }
      if (err instanceof TtsUnavailableError) {
        setErrorMessage(err.reason);
      } else {
        setErrorMessage(err instanceof Error ? err.message : String(err));
      }
      setState('error');
    } finally {
      if (abortRef.current === ac) {
        abortRef.current = null;
      }
    }
  }, [services, ttsQueue, state, text, effectiveVoice, cleanup]);

  if (services === null || !services.voice.isAvailable()) {
    return null;
  }

  // V4.7 (2026-06-02): when a shared queue is mounted, defer the glyph
  // logic to the queue's own state so the spinner shows during the
  // Kokoro fetch round-trip (the local `state` only flips when this
  // specific component dispatches via the V1 fallback path).
  const queueLoading = ttsQueue !== null && ttsQueue.state === 'loading';
  const queuePlaying = ttsQueue !== null && ttsQueue.state === 'playing';
  const isPlaying = queuePlaying || state === 'playing';
  const isLoading = queueLoading || state === 'loading';
  const buttonGlyph = isLoading ? '◐' : isPlaying ? '⏸' : '▶';
  const buttonLabel = isLoading
    ? 'Loading narration'
    : isPlaying
      ? 'Stop reading'
      : 'Read aloud';

  // V4.7 (2026-06-02): expose the effective state (queue or local) on
  // the wrapper so the CSS spinner targets the right component.
  const effectiveState: ReadAloudState = isLoading
    ? 'loading'
    : isPlaying
      ? 'playing'
      : state === 'error'
        ? 'error'
        : 'idle';

  return (
    <div
      className="br-read-aloud"
      data-testid={testidPrefix}
      data-state={effectiveState}
    >
      <button
        type="button"
        className="br-read-aloud-toggle"
        data-testid={`${testidPrefix}-toggle`}
        onClick={() => {
          void handleClick();
        }}
        aria-pressed={isPlaying}
        aria-label={buttonLabel}
        title={buttonLabel}
        disabled={isLoading}
      >
        <span className="br-read-aloud-glyph" aria-hidden="true">
          {buttonGlyph}
        </span>
      </button>
      {isPlaying && audioUrl !== null && (
        <audio
          ref={(el) => {
            audioRef.current = el;
          }}
          className="br-read-aloud-audio"
          data-testid={`${testidPrefix}-audio`}
          src={audioUrl}
          controls
          autoPlay
          onEnded={() => cleanup()}
          onError={() => {
            setErrorMessage('audio playback failed');
            cleanup({ keepState: true });
            setState('error');
          }}
        />
      )}
      {state === 'error' && errorMessage !== null && (
        <p
          className="br-label br-read-aloud-error"
          data-testid={`${testidPrefix}-error`}
          role="alert"
        >
          {errorMessage}
        </p>
      )}
    </div>
  );
}

export default ReadAloudControl;
